import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { ShippingRulePresetsApiAdapter } from '../adapters/ShippingRulePresetsApiAdapter';
import { ShippingRulePresetsFilters } from '../models/ShippingRulePreset';

// Hook que gestiona la obtención y paginación de los presets de reglas de envío
export const useShippingRulePresets = (filters: ShippingRulePresetsFilters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const adapter = new ShippingRulePresetsApiAdapter(httpClient);

  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [previousPresets, setPreviousPresets] = useState<any[]>([]);

  const { data, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isInitialLoading } =
    useInfiniteQuery({
      queryKey: ['shipping-rule-presets', filters],
      queryFn: ({ pageParam = 1 }) => adapter.getPresetsPaginated({ ...filters, page: pageParam as number }),
      getNextPageParam: (lastPage) => lastPage.nextPage,
      initialPageParam: 1,
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      placeholderData: (prev) => prev,
    });

  const presets = useMemo(() => data?.pages.flatMap((page) => page.presets) ?? [], [data]);
  const total = data?.pages?.[0]?.total ?? 0;

  // Marcar que ya pasó la primera carga
  useEffect(() => {
    if (presets.length > 0 && isFirstLoad) {
      setIsFirstLoad(false);
    }
  }, [presets.length, isFirstLoad]);

  // Mantener datos anteriores para que no desaparezcan durante búsquedas
  useEffect(() => {
    if (presets.length > 0) {
      setPreviousPresets(presets);
    }
  }, [presets]);

  const displayPresets = isFetching && !isFirstLoad && previousPresets.length > 0 ? previousPresets : presets;

  return {
    presets: displayPresets,
    isLoading: isFirstLoad && isInitialLoading,
    isFetching,
    error,
    total,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
};
